import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api, ApiHttpError } from '~/lib/api/client'
import type {
  Holiday,
  HolidayCreatePayload,
  HolidayUpdatePayload,
  HolidaysResponse,
} from '~/lib/api/types'

export const HOLIDAYS_QUERY_KEY = ['holidays'] as const

export function useHolidays() {
  return useQuery<HolidaysResponse, ApiHttpError>({
    queryKey: HOLIDAYS_QUERY_KEY,
    queryFn: () => api.listHolidays(),
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  })
}

export function useInvalidateHolidays() {
  const qc = useQueryClient()
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY })
  }, [qc])
}

export function useCreateHoliday() {
  const qc = useQueryClient()
  return useMutation<Holiday, ApiHttpError, HolidayCreatePayload>({
    mutationFn: payload => api.createHoliday(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY }),
  })
}

export function useUpdateHoliday() {
  const qc = useQueryClient()
  return useMutation<
    Holiday,
    ApiHttpError,
    { id: string; payload: HolidayUpdatePayload }
  >({
    mutationFn: ({ id, payload }) => api.updateHoliday(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY }),
  })
}

export function useDeleteHoliday() {
  const qc = useQueryClient()
  return useMutation<void, ApiHttpError, string>({
    mutationFn: id => api.deleteHoliday(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY }),
  })
}
